/**
 * useUpcomingReminders — returns the next mentor session and the next event
 * the member has RSVP'd to, both within the next 48 hours. Used by the
 * Account dashboard reminder card.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface Reminder {
  id: string;
  kind: "mentor" | "event";
  title: string;
  startsAt: string;
  href: string;
}

const WINDOW_MS = 48 * 60 * 60 * 1000;

export function useUpcomingReminders() {
  const { user } = useAuth();
  const [items, setItems] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const nowIso = new Date().toISOString();
      const horizon = new Date(Date.now() + WINDOW_MS).toISOString();

      const out: Reminder[] = [];

      try {
        const { data: sessions } = await supabase
          .from("mentor_sessions" as never)
          .select("id, scheduled_at, mentor_id, mentee_id")
          .or(`mentor_id.eq.${user.id},mentee_id.eq.${user.id}`)
          .gte("scheduled_at", nowIso)
          .lte("scheduled_at", horizon)
          .order("scheduled_at", { ascending: true })
          .limit(3);
        for (const s of (sessions ?? []) as Array<{ id: string; scheduled_at: string }>) {
          out.push({
            id: s.id,
            kind: "mentor",
            title: "Mentor session",
            startsAt: s.scheduled_at,
            href: `/mentor-sessions`,
          });
        }
      } catch {
        /* ignore */
      }

      try {
        const { data: rsvps } = await supabase
          .from("event_rsvps" as never)
          .select("event_id, events(id, title, starts_at)")
          .eq("user_id", user.id)
          .limit(10);
        for (const r of (rsvps ?? []) as Array<{
          events: { id: string; title: string; starts_at: string } | null;
        }>) {
          const ev = r.events;
          if (!ev) continue;
          const t = new Date(ev.starts_at).getTime();
          if (t >= Date.now() && t <= Date.now() + WINDOW_MS) {
            out.push({
              id: ev.id,
              kind: "event",
              title: ev.title,
              startsAt: ev.starts_at,
              href: `/events/${ev.id}`,
            });
          }
        }
      } catch {
        /* ignore */
      }

      out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      setItems(out);
      setLoading(false);
    })();
  }, [user]);

  return { items, loading };
}
